import { RouterProvider } from 'react-router-dom';
import { router } from './Routes';
import { AuthProvider } from '@/auth';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { PostHogProvider } from 'posthog-js/react';
import {
  POSTHOG_API_KEY,
  isPostHogEnabled,
  posthogOptions,
} from '@/posthog-analytics/posthog';
import { PostHogUserSync } from '@/posthog-analytics/PostHogUserSync';
import { ApiError } from '@/api/errors';
import { initSdk } from '@/api/setup';
import { AppErrorBoundary } from '@/common/components/AppErrorBoundary';

initSdk();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => !(error instanceof ApiError) && failureCount < 3,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * PostHogWrapper - Wraps children with PostHogProvider on allowed domains
 * PostHog is always mounted (on allowed domains) to avoid reinitialization.
 * User identification is handled by PostHogUserSync based on auth state.
 */
function PostHogWrapper({ children }: { children: React.ReactNode }) {
  if (!isPostHogEnabled) {
    return <>{children}</>;
  }

  return (
    <PostHogProvider apiKey={POSTHOG_API_KEY} options={posthogOptions}>
      {children}
    </PostHogProvider>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <PostHogWrapper>
        <AuthProvider>
          <PostHogUserSync />
          <div className="font-poppins bg-secondary">
            <AppErrorBoundary boundary="root">
              <RouterProvider router={router} />
            </AppErrorBoundary>
          </div>
        </AuthProvider>
      </PostHogWrapper>
    </QueryClientProvider>
  );
}

App.displayName = 'App';
export default App;
